import { Collection, getFirebase } from 'firestorter';
import moment from 'moment';

import BaseDocument from '../../Model/BaseDocument';
import InternalAssignment from '../../Model/InternalAssignment';
import { AssignmentRole } from '../../utils/internalAssignments';
import Collections from '../../utils/collections';
import LeadHistory from './LeadHistory';

const MAX_IN_FILTER_VALUES = 10;

/**
 * The state of a lead.
 */
const LeadState = {
  NEW: 'NEW',
  CONTACTED: 'CONTACTED',
  IN_DIALOGUE: 'IN_DIALOGUE',
  FIRST_MEETING: 'FIRST_MEETING',
  OFFER_SENT: 'OFFER_SENT',
  ON_HOLD: 'ON_HOLD',
  LOST: 'LOST',
  PENDING: 'PENDING',
  WON: 'WON',
  IS_CONTACTED: 'IS_CONTACTED',
  UNQUALIFIED: 'UNQUALIFIED',
  CALENDLY_SCHEDULED: 'CALENDLY_SCHEDULED',
  FOLLOW_UP: 'FOLLOW_UP',
};

/**
 * The sales stage of a lead.
 */
const SalesStage = {
  NEW_LEAD: 'NEW_LEAD',
  FIRST_MESSAGE: 'FIRST_MESSAGE',
  SECOND_MESSAGE: 'SECOND_MESSAGE',
  IN_DIALOGUE: 'IN_DIALOGUE',
  UNQUALIFIED: 'UNQUALIFIED',
  LOST: 'LOST',
  WON: 'WON',
  PENDING: 'PENDING',
  CALENDLY_SCHEDULED: 'CALENDLY_SCHEDULED',
  DUPLICATE: 'DUPLICATE',
  MANUAL: 'MANUAL',
  FOLLOW_UP: 'FOLLOW_UP',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
};

const FinalStatusSalesStages = [
  SalesStage.LOST,
  SalesStage.WON,
  SalesStage.UNQUALIFIED,
  SalesStage.PENDING,
];

const InitialLeadState = [
  LeadState.NEW,
  LeadState.CONTACTED,
  LeadState.IN_DIALOGUE,
  LeadState.FIRST_MEETING,
  LeadState.OFFER_SENT,
  LeadState.ON_HOLD,
  LeadState.PENDING,
  LeadState.IS_CONTACTED,
  LeadState.CALENDLY_SCHEDULED,
];

const ISDashboardSalesStages = [
  SalesStage.NEW_LEAD,
  SalesStage.FOLLOW_UP,
  SalesStage.PAYMENT_PENDING,
  SalesStage.IN_DIALOGUE,
  SalesStage.FIRST_MESSAGE,
  SalesStage.SECOND_MESSAGE,
  SalesStage.MANUAL,
  ...FinalStatusSalesStages,
];

const AllLeadStates = Object.values(LeadState);
const AllSalesStages = Object.values(SalesStage);

const DefaultLeadsSalesStageFilters = [
  SalesStage.NEW_LEAD,
  SalesStage.FIRST_MESSAGE,
  SalesStage.SECOND_MESSAGE,
  SalesStage.IN_DIALOGUE,
  SalesStage.PENDING,
  SalesStage.DUPLICATE,
  SalesStage.CALENDLY_SCHEDULED,
  SalesStage.MANUAL,
  SalesStage.FOLLOW_UP,
  SalesStage.PAYMENT_PENDING,
];

/**
 * Split the given (or default) sales stage filters into chunks of at most MAX_IN_FILTER_VALUES, the shape both
 * getAllLeadsByCoachList and getOldestLeadDateByCoachList fan out a Firestore 'in' query per chunk.
 *
 * @param {Array} [salesStageFilters = []] The list of sales stages to filter the leads by
 * @returns {Array<Array<string>>} The sales stage filters split into chunks
 */
const buildSalesStageFilterChunks = (salesStageFilters = []) => {
  const selectedSalesStageFilters = Array.from(new Set(
    salesStageFilters.length ? salesStageFilters : DefaultLeadsSalesStageFilters,
  ));
  const chunks = [];
  for (let index = 0; index < selectedSalesStageFilters.length; index += MAX_IN_FILTER_VALUES) {
    chunks.push(selectedSalesStageFilters.slice(index, index + MAX_IN_FILTER_VALUES));
  }
  return chunks;
};

/**
 * The contact provider for a lead.
 */
const ContactProvider = {
  iMessage: 'iMessage',
  WhatsApp: 'WhatsApp',
  EMAIL: 'Email',
  SMS: 'SMS',
  ANY: 'ANY',
  SENDBLUE: 'SendBlue',
  INSTAGRAM: 'Instagram',
  /** The Avida RCS sender. */
  RCS: 'RCS',
};

const LocalContactProvider = {
  [ContactProvider.iMessage]: ContactProvider.iMessage,
  [ContactProvider.WhatsApp]: ContactProvider.WhatsApp,
};

const ContactOptions = {
  EMAIL: ContactProvider.EMAIL,
  iMessage: ContactProvider.iMessage,
};

const LeadHistoryType = {
  NOTE: 'NOTE',
  STATE_CHANGE: 'STATE_CHANGE',
  SALES_STAGE_CHANGE: 'SALES_STAGE_CHANGE',
  CONTACT: 'CONTACT',
  MSG: 'MSG',
  MSG_SENT: 'MSG_SENT',
  CALL: 'CALL',
  CALENDLY_SCHEDULED: 'CALENDLY_SCHEDULED',
  DIRECT_PURCHASE_LINK_SENT: 'DIRECT_PURCHASE_LINK_SENT',
  SENT_TO_RESCHEDULE: 'SENT_TO_RESCHEDULE',
  RESCHEDULED_MANUALLY: 'RESCHEDULED_MANUALLY',
  RESPONSE: 'RESPONSE',
  CALL_PLACED: 'CALL_PLACED',
  NO_SHOW: 'NO_SHOW',
  CALL_OUTCOME: 'CALL_OUTCOME',
  CLAIMED_BY_AGENT: 'CLAIMED_BY_AGENT',
  COMMENT: 'COMMENT',
  CALL_CANCELLED: 'CALL_CANCELLED',
  INTENT_FLAG_CHANGE: 'INTENT_FLAG_CHANGE',
};

const LeadCallOutcome = {
  FOLLOW_UP: 'FOLLOW_UP',
  NOT_ANSWERED: 'NOT_ANSWERED',
  LOST: 'LOST',
  WON: 'WON',
  PAYMENT_PENDING: 'PAYMENT_PENDING',
};

const ClaimRejectReason = {
  MISSING_LEAD_CONTEXT: 'missing-lead-context',
  ALREADY_CLAIMED: 'already-claimed',
};

/**
 * Outcomes that are fixed when opened from Won / Payment Pending stage flows (dropdown locked).
 * @param {string} [outcome]
 * @returns {boolean}
 */
const isLockedLeadCallOutcome = (outcome) => [
  LeadCallOutcome.WON,
  LeadCallOutcome.PAYMENT_PENDING,
].includes(outcome);

const LeadLostReason = {
  AFFORDABILITY: 'AFFORDABILITY',
  COMMITMENT_PERIOD: 'COMMITMENT_PERIOD',
  NOT_RIGHT_TIME: 'NOT_RIGHT_TIME',
  FORMAT_NOT_FIT: 'FORMAT_NOT_FIT',
  SHOPPING_AROUND: 'SHOPPING_AROUND',
  NO_VALUE: 'NO_VALUE',
  NOT_SERIOUS: 'NOT_SERIOUS',
  WANTS_FREE: 'WANTS_FREE',
  HEALTH_INJURY: 'HEALTH_INJURY',
};

const LeadFollowUpReason = {
  TIME_TO_THINK: 'TIME_TO_THINK',
  SPEAK_TO_PARTNER: 'SPEAK_TO_PARTNER',
  ASSESS_FINANCES: 'ASSESS_FINANCES',
  WAITING_FOR_PAYDAY: 'WAITING_FOR_PAYDAY',
  REVIEW_PROGRAM_DETAILS: 'REVIEW_PROGRAM_DETAILS',
  MEDICAL_CLEARANCE: 'MEDICAL_CLEARANCE',
  WANTS_PROOF: 'WANTS_PROOF',
  TECHNICAL_ISSUE: 'TECHNICAL_ISSUE',
  REQUESTED_FOLLOW_UP: 'REQUESTED_FOLLOW_UP',
  TRAVEL_OR_LIFE_EVENT: 'TRAVEL_OR_LIFE_EVENT',
  CONTRACT_CREATED_PENDING_ONBOARDING: 'CONTRACT_CREATED_PENDING_ONBOARDING',
};

/**
 * The IS rep's read on client commitment at the moment of close (RAG).
 */
const LeadIntentFlag = {
  GREEN: 'GREEN',
  AMBER: 'AMBER',
  RED: 'RED',
};

const LeadIntentFlagLabel = {
  [LeadIntentFlag.GREEN]: 'Green',
  [LeadIntentFlag.AMBER]: 'Amber',
  [LeadIntentFlag.RED]: 'Red',
};

const MAX_TIME_THRESHOLD_TO_CLAIM_LEAD = 15;

/**
 * Class representing a lead.
 *
 * @class Lead
 * @extends BaseDocument
 */
class Lead extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.LEADS}/${id}`, opts);
  }

  /**
   * Get the first name.
   * @return {string}
   */
  get firstName() {
    return this.data.firstName || '';
  }

  /**
   * Get the last name.
   * @return {string}
   */
  get lastName() {
    return this.data.lastName || '';
  }

  /**
   * Get the full name.
   * @return {string}
   */
  get name() {
    const name = `${this.firstName.trim()} ${this.lastName.trim()}`;
    return name || this.data.name;
  }

  /**
   * Get the email.
   * @return {string}
   */
  get email() {
    return this.data.email;
  }

  /**
   * Get the gender.
   * @return {string}
   */
  get gender() {
    return this.data.gender;
  }

  /**
   * Get the lead submitted date and time in 'YYYY-MM-DD HH:mm:ss' format
   * @return {string}
   */
  get submitDate() {
    return this.data.submitDate;
  }

  /**
   * Get the lead follow-up date
   * @return {Date|string}
   */
  get followUpDate() {
    return this.data.followUpDate;
  }

  /**
   * Get the lead start date
   * @return {Date|string}
   */
  get startDate() {
    return this.data.startDate;
  }

  /**
   * Get anonymous user id for tracking purposes in Paperform integration
   * @return {string}
   */
  get anonid() {
    return this.data.anonid;
  }

  /**
   * Get aggregating questions and answers for the questions in Paperform integration
   * @return {string}
   */
  get responses() {
    return this.data.responses;
  }

  /**
   * Get the lead submitted answers
   * @return {Array}
   */
  get answers() {
    return this.data.answers;
  }

  /**
   * Get the state of the lead
   * @return {string}
   */
  get state() {
    return this.data.state;
  }

  /**
   * Get the sales stage of the lead
   * @return {string}
   */
  get salesStage() {
    return this.data.salesStage;
  }

  /**
   * Get the name of assigned sendblue number for the lead
   */
  get sendBlueName() {
    return this.data.sendBlueName;
  }

  /**
   * Get the name of assigned sendblue number for the lead
   */
  get sendBlueNumber() {
    return this.data.sendBlueNumber || '';
  }

  /**
   * Get the lead submitted country
   * @return {string}
   */
  get country() {
    return this.data.country;
  }

  /**
   * Get the phone number
   * @param {string}
   */
  get phoneNumber() {
    return this.data.phoneNumber;
  }

  /**
   * Get the timezone of the lead's submitted location
   * @return {string}
   */
  get timezone() {
    return this.data.timezone;
  }

  /**
   * Get the notes of the lead added by coach or IS
   * @return {string}
   */
  get notes() {
    return this.data.notes;
  }

  /**
   * Get the removed status
   * @return {boolean}
   */
  get removed() {
    return !!this.data.removed;
  }

  /**
   * Get the assigned coach's ID
   * @return {string}
   */
  get coach() {
    return this.data.coach;
  }

  /**
   * Get the last contacted time stamp
   * @return {Date|string}
   */
  get lastContactedAt() {
    return this.data.lastContactedAt || '';
  }

  /**
   * Get the user name of the agent who last contacted the lead
   * @return {string}
   */
  get lastContactedBy() {
    return this.data.lastContactedBy || '';
  }

  /**
   * Get the user ID of the agent who last contacted the lead
   * @return {string}
   */
  get lastContactedByUserId() {
    return this.data.lastContactedByUserId || '';
  }

  /**
   * Get the message provider used last time to contact the lead
   * @return {string}
   */
  get lastContactedMsgProvider() {
    return this.data.lastContactedMsgProvider || '';
  }

  /**
   * Get the user name of the server last message sent to the lead
   * @return {string}
   */
  get lastSendAs() {
    return this.data.lastSendAs || '';
  }

  /**
   * Get the user ID of the server last message sent to the lead
   * @return {string}
   */
  get lastSendAsUserId() {
    return this.data.lastSendAsUserId || '';
  }

  /**
   * Get the server contact number last message sent of the lead
   * @return {string}
   */
  get msgServerContactNo() {
    return this.data.msgServerContactNo || '';
  }

  /**
   * Get the source of lead form found
   * @return {string}
   */
  get source() {
    return this.data.answers?.find((answerData) => answerData.custom_key === 'source')?.value || '-';
  }

  /**
   * If lead didn't show up for a scheduled call or not
   * @return {boolean}
   */
  get noShow() {
    return !!this.data.noShow;
  }

  /**
   * Get the lead's last assigned time
   * @return {Date|string}
   */
  get assignedAt() {
    return this.data.assignedAt;
  }

  /**
   * The challenge this lead has been offered, if any.
   *
   * A challenge is sold from its BASE contract, so this is that contract's id. Nothing is created
   * for the lead until they pay through the onboarding link — this only records which challenge
   * the rep picked, so reopening the lead lands back on it.
   *
   * @return {string}
   */
  get selectedChallengeContractId() {
    return this.data.selectedChallengeContractId || '';
  }

  /**
   * Save the challenge the rep picked for this lead.
   *
   * @param {string} contractId The challenge's BASE contract id, or '' to clear the choice.
   * @return {Promise<void>}
   */
  setSelectedChallengeContract(contractId) {
    return this.updateFields({ selectedChallengeContractId: contractId || '' });
  }

  /**
   * The Inside Sales rep working this lead.
   *
   * @return {string}
   */
  get isAgent() {
    return this.data.isAgent || '';
  }

  /**
   * Record the Inside Sales rep working this lead.
   *
   * Set when a rep claims the lead, books it a call, or writes it a contract — whichever happens
   * first does not matter, the field names whoever is on it now. A challenge sale reads it back at
   * checkout to credit the commission, since no contract is written for the lead until they pay.
   *
   * @param {string} agentId The rep's user id.
   * @param {string} agentName The rep's full name, shown on the lead lists.
   * @return {Promise<void>}
   */
  setInsideSalesAgent(agentId, agentName = '') {
    return this.updateFields({ isAgent: agentId, isAgentName: agentName });
  }

  /**
   * Get the lead's claimed agent userId
   * @return {string}
   */
  get claimedBy() {
    return this.data.claimedBy;
  }

  /**
   * Get the lead's claimed time
   * @return {Date|string}
   */
  get claimedAt() {
    return this.data.claimedAt;
  }

  /**
   * Get the lead's cal.com meeting time
   * @return {Date|string}
   */
  get calendlyMeetingTime() {
    return this.data.calendlyMeetingTime;
  }

  /**
   * Get the lead's user activated date
   * @return {Date|string}
   */
  get userActivatedAt() {
    return this.data.userActivatedAt;
  }

  /**
   * Get the lead's contract created date
   * @return {Date|string}
   */
  get contractCreatedAt() {
    return this.data.contractCreatedAt;
  }

  /**
   * Get the lead's won date
   * @return {Date}
   */
  get wonAt() {
    return this.data.wonAt;
  }

  /**
   * Get the lead's pricing instruction
   * @return {string}
   */
  get pricingInstruction() {
    return this.data.pricingInstruction;
  }

  /**
   * Get the call attempts recorded for this lead.
   * @return {Array}
   */
  get callAttempts() {
    return this.data.callAttempts || [];
  }

  /**
   * Get whether automated SDR is enabled for this lead
   * @return {boolean}
   */
  get useAutomatedSdr() {
    return this.data.useAutomatedSdr;
  }

  /**
   * Get the reason the lead was escalated from aiSDR to a human SDR.
   * @return {string|undefined}
   */
  get escalationReason() {
    return this.data.escalationReason;
  }

  /**
   * Whether this lead was previously escalated from aiSDR to a human SDR
   * (i.e. aiSDR is off AND an escalation reason is set). Used to gate the
   * "Return to AI SDR" action so it never shows for leads that were never on aiSDR.
   * @return {boolean}
   */
  get isEscalatedFromAiSdr() {
    return this.useAutomatedSdr === false && !!this.escalationReason;
  }

  /**
   * Get the intent flag (RAG) captured at close
   * @return {string}
   */
  get intentFlag() {
    return this.data.intentFlag || '';
  }

  /**
   * Set the intent flag captured at close. The current value lives on the lead doc; each
   * change is also recorded as a timestamped lead history entry, so prior values are retained.
   *
   * @param {string} intentFlag The intent flag (see LeadIntentFlag)
   * @param {Object} opts
   * @param {string} opts.userId The user ID of the rep setting the flag
   * @param {string} opts.userName The user name of the rep setting the flag
   * @return {Promise<void>}
   */
  async setIntentFlag(intentFlag, {
    userId,
    userName,
  }) {
    await this.updateFields({ intentFlag });
    await this.addItemToHistory({
      value: intentFlag,
      activityType: LeadHistoryType.INTENT_FLAG_CHANGE,
      createdBy: userId,
      createdByUserName: userName,
    });
  }

  /**
   * Append a call attempt to the lead document.
   * @param {Object} attempt
   * @return {Promise<void>}
   */
  async addCallAttempt(attempt) {
    await this.updateFields({
      callAttempts: [...this.callAttempts, attempt],
    });
  }

  /**
   * Update lead as a no show
   * @return {Promise<void>}
   */
  async setNoShow() {
    await this.updateFields({
      noShow: true,
    });
  }

  /**
   * Updates the state and sales stage of a lead based on the provided sales stage.
   * It also optionally adds an item to the lead's history if a 'userId' and 'userName' is provided.

   * @param {string} salesStage The sales stage of the lead
   * @param {Object} opts Optional parameters for setting the state
   * @param {string} opts.userId The user ID of the user setting the state
   * @param {string} opts.userName The user name of the user setting the state
   * @param {string} opts.reason The reason for the sales stage change
   * @param {string} opts.note The note for the sales stage change
   * @return {Promise<void>}
   */
  setSalesStage(salesStage, {
    userId = '',
    userName = '',
    reason = '',
    note = '',
  } = {}) {
    let newLeadState;
    switch (salesStage) {
      case SalesStage.NEW_LEAD:
        newLeadState = LeadState.NEW;
        break;
      case SalesStage.UNQUALIFIED:
        newLeadState = LeadState.UNQUALIFIED;
        break;
      case SalesStage.LOST:
        newLeadState = LeadState.LOST;
        break;
      case SalesStage.WON:
        newLeadState = LeadState.WON;
        break;
      case SalesStage.PENDING:
      case SalesStage.MANUAL:
        newLeadState = LeadState.PENDING;
        break;
      default:
        newLeadState = LeadState.IS_CONTACTED;
    }

    this.updateFields({
      salesStage,
      state: newLeadState,
      ...(salesStage === SalesStage.LOST && { lostAt: new Date() }),
    });

    if (userId && userName) {
      this.addItemToHistory({
        reason,
        note,
        value: salesStage,
        activityType: LeadHistoryType.SALES_STAGE_CHANGE,
        createdBy: userId,
        createdByUserName: userName,
      });
    }
  }

  /**
   * Updates the state and sales stage of a lead based on the provided state.
   * It also optionally adds an item to the lead's history if a 'userId' and 'userName' is provided.

   * @param {string} state The state of the lead
   * @param {Object} opts Optional parameters for setting the state
   * @param {string} opts.userId The user ID of the user setting the state
   * @param {string} opts.userName The user name of the user setting the state
   * @return {Promise<void>}
   */
  setState(state, {
    userId = '',
    userName = '',
  } = {}) {
    let newSalesStage;
    switch (state) {
      case LeadState.NEW:
        newSalesStage = SalesStage.NEW_LEAD;
        break;
      case LeadState.UNQUALIFIED:
        newSalesStage = SalesStage.UNQUALIFIED;
        break;
      case LeadState.LOST:
        newSalesStage = SalesStage.LOST;
        break;
      case LeadState.WON:
        newSalesStage = SalesStage.WON;
        break;
      default:
        newSalesStage = SalesStage.PENDING;
    }
    this.updateFields({
      state,
      salesStage: newSalesStage,
      ...(state === LeadState.LOST && { lostAt: new Date() }),
    });

    if (userId && userName) {
      this.addItemToHistory({
        value: state,
        activityType: LeadHistoryType.STATE_CHANGE,
        createdBy: userId,
        createdByUserName: userName,
      });
    }
  }

  /**
   * Updates the follow up date of the lead
   * @param {Date} followUpDate The follow up date of the lead
   * @return {Promise<void>}
   */
  async setFollowUpDate(followUpDate) {
    await this.updateFields({ followUpDate });
  }

  /**
   * Add an item to the lead history based on the provided item object
   * It also updates the last contacted data of the lead if the activity type is MSG.

   * @param {Object} item The item to add to the lead history
   * @param {Object|string} item.value The value of the item
   * @param {string} item.reason The reason for the add item
   * @param {string} item.note The note for the add item
   * @param {string} item.activityType The type of activity
   * @param {string} item.createdBy The user ID of the user who created the item
   * @param {string} item.createdByUserName The user name of the user who created the item
   * @param {Date} item.createdAt The date and time the item was created
   * @return {Promise<void>}
   */
  async addItemToHistory({
    value,
    reason,
    note,
    activityType,
    createdBy,
    createdByUserName,
    createdAt,
  }) {
    const item = {
      leadId: this.id,
      createdAt: createdAt || new Date(),
      createdBy,
      createdByUserName,
      activityType,
      value,
      coachId: this.coach,
    };

    if (reason) {
      item.reason = reason;
    }
    if (note) {
      item.note = note;
    }
    await LeadHistory.addLeadHistoryItem(item);

    // Add last contact data to lead doc
    if (activityType === LeadHistoryType.MSG) {
      await this.updateFields({
        lastContactedAt: new Date(),
        lastContactedBy: createdByUserName,
        lastContactedByUserId: createdBy,
        // A lead on RCS stays there: the server sends this reply over RCS whatever provider the chat
        // passed, and the server alone moves a lead off RCS (when it texts the SendBlue line).
        ...(this.lastContactedMsgProvider !== ContactProvider.RCS && { lastContactedMsgProvider: value.msgProvider }),
        ...(value.sendAs && {
          lastSendAs: value.sendAsUserName,
          lastSendAsUserId: value.sendAs,
        }),
      });
    }
  }

  /**
   * Set the notes
   * @param {string} notes The notes for the lead
   * @return {Promise<void>}
   */
  setNotes(notes) {
    this.updateFields({
      notes,
    });
  }

  /**
   * Set the lead's removed status
   * @param {boolean} removed The removed status of the lead
   * @return {Promise<void>}
   */
  setRemoved(removed) {
    this.updateFields({
      removed,
      removedAt: new Date(),
    });
  }

  /**
   * Get the the historical records associated with the lead.
   * It includes lead's progression, interactions, and modifications over time.
   * @returns {Promise<Array>} The history documents for the lead.
   */
  async getHistory() {
    return LeadHistory.getLeadHistory(this.id);
  }

  /**
   * Get the specified coach's lead for the specified email. People enquire with more than
   * one coach before signing up, so email alone matches leads belonging to other coaches.

   * @param {string} email The email of the lead
   * @param {string} coachId The coach whose lead is wanted
   * @returns {Promise<Lead|void>} The lead document if it exists, or else null.
   */
  static async getLeadByEmail(email, coachId) {
    const leadsCollection = new Collection(Collections.LEADS, {
      createDocument: ({ id }, opts) => new Lead(id, opts),
      query: (ref) => ref
        .where('email', '==', email)
        .where('coach', '==', coachId)
        .limit(1),
    });
    await leadsCollection.fetch();
    return leadsCollection.hasDocs ? leadsCollection.docs[0] : null;
  }

  /**
   * Get all the leads for the specified list of coaches and from the specified date.

   * @param {Array} [coachesList = []] The list of coach IDs
   * @param {Date} fromDate The date from which to fetch the leads (inclusive)
   * @param {string} isAgentId The ID of the IS agent to filter the leads by
   * @param {Array} [salesStageFilters = []] The list of sales stages to filter the leads by
   * @param {string} [fromDateField = 'submitDate'] The field to use for the date filter
   * @param {Object} [pagination = {}] Pagination options
   * @param {Date} [pagination.toDate] Upper date bound (exclusive). Used to fetch just the next older slice of a
   *   date range already partially loaded — e.g. re-fetching the same fromDate/toDate shape the 7/30/60-day
   *   filters already use unbounded, just with both ends specified instead of only a lower bound.
   * @param {Object} [pagination.oldestDateByCoach] Map of coachId -> that coach's own oldest lead date (from
   *   getOldestLeadDateByCoachList). When a coach's whole window falls at or before their oldest lead, that
   *   coach's sub-queries are skipped without a Firestore read — there's nothing older left for them to find.
   * @returns {Observable<Collection<Lead>>}
   */
  static async getAllLeadsByCoachList(
    coachesList = [],
    fromDate,
    isAgentId,
    salesStageFilters = [],
    fromDateField = 'submitDate',
    pagination = {},
  ) {
    const { toDate, oldestDateByCoach } = pagination;
    // Make sure we process an array of IDs.
    const coachIds = [...coachesList];
    const salesStageFilterChunks = buildSalesStageFilterChunks(salesStageFilters);

    /*
      Fetch all the leads for the specified coaches. We control the sales stage for the lead, so that we don't get
      leads we are not going to use (like disqualified leads).

      Each coach/sales-stage-chunk combination is its own Firestore sub-query, fanned out and combined by the
      caller — unbounded by count, same as the 7/30/60-day filters already are, relying on the fromDate/toDate
      window (not a per-sub-query limit) to keep each individual fetch reasonably sized.
    */
    const queriesPromises = () => coachIds.flatMap((coachId) => salesStageFilterChunks.map((salesStageChunk) => {
      // The entire window is at or before this coach's own oldest lead — there's nothing left to find for them,
      // so skip the read instead of re-querying an exhausted coach on every future Load More click.
      const coachOldestDate = oldestDateByCoach?.[coachId];
      if (toDate && coachOldestDate && toDate <= coachOldestDate) {
        return Promise.resolve({ docs: [] });
      }

      return new Collection(Collections.LEADS, {
        createDocument: ({ id }, opts) => new Lead(id, { ...opts, disableObserverRefCount: true }),
        query: (ref) => {
          let query = ref
            .where('coach', '==', coachId)
            .where('salesStage', 'in', salesStageChunk);
          if (fromDate) {
            query = query.where(fromDateField, '>=', fromDate);
          }
          if (toDate) {
            query = query.where(fromDateField, '<', toDate);
          }
          if (isAgentId) {
            query = query.where('isAgent', '==', isAgentId);
          }
          // Only impose an order when there's an actual date window to page over — an unrelated caller with no
          // fromDate/toDate (e.g. the payment-pending fetch) shouldn't have this fan-out silently start dropping
          // any lead missing submitDate, which is what an unconditional orderBy would do (Firestore excludes
          // documents missing the ordered field from ordered query results).
          if (fromDate || toDate) {
            query = query.orderBy(fromDateField, 'desc');
          }
          return query;
        },
      }).fetch();
    }));

    /*
      Wait for the queries to complete before returning the observable.= value. The components using this property
      will have to use observer correctly to get all the data.
    */
    const leadsCollections = await Promise.all(queriesPromises());

    return leadsCollections;
  }

  /**
   * Get the oldest lead date per coach across every sales-stage-chunk sub-query getAllLeadsByCoachList would fan
   * out into for the given coaches, ignoring any pagination already done. Lets callers show users the true bottom
   * of the "All" date range, and skip re-querying a coach once their own window is exhausted.
   *
   * @param {Array} [coachesList = []] The list of coach IDs
   * @param {string} isAgentId The ID of the IS agent to filter the leads by
   * @param {Array} [salesStageFilters = []] The list of sales stages to filter the leads by
   * @param {string} [fromDateField = 'submitDate'] The field to read the oldest date from
   * @returns {Promise<Object<string, string|null>>} Map of coachId -> that coach's oldest date (or null if they
   *   have no matching leads at all)
   */
  static async getOldestLeadDateByCoachList(
    coachesList = [],
    isAgentId,
    salesStageFilters = [],
    fromDateField = 'submitDate',
  ) {
    const coachIds = [...coachesList];
    const salesStageFilterChunks = buildSalesStageFilterChunks(salesStageFilters);

    const entries = await Promise.all(coachIds.map(async (coachId) => {
      const chunkCollections = await Promise.all(salesStageFilterChunks.map((salesStageChunk) => (
        new Collection(Collections.LEADS, {
          createDocument: ({ id }, opts) => new Lead(id, { ...opts, disableObserverRefCount: true }),
          query: (ref) => {
            let query = ref
              .where('coach', '==', coachId)
              .where('salesStage', 'in', salesStageChunk);
            if (isAgentId) {
              query = query.where('isAgent', '==', isAgentId);
            }
            return query.orderBy(fromDateField, 'asc').limit(1);
          },
        }).fetch()
      )));

      const oldestDate = chunkCollections.reduce((oldest, collection) => {
        const [oldestDoc] = collection.docs;
        if (!oldestDoc) {
          return oldest;
        }
        return (!oldest || oldestDoc[fromDateField] < oldest) ? oldestDoc[fromDateField] : oldest;
      }, null);

      return [coachId, oldestDate];
    }));

    return Object.fromEntries(entries);
  }

  /**
   * Get all the leads for the specified role
   * @param {AssignmentRole} [role = IS] role The role for which to fetch the leads.
   * @returns {Observable<Collection<Lead>>}
   */
  static async getAllLeadsForRole(role = AssignmentRole.INSIDE_SALES) {
    // Get all the assignments for the specified role.
    const assignments = await InternalAssignment.getAllCurrentAssignmentsByRole(role);

    // Extract the list of coaches from the assignments. We are going to retrieve the leads for these coaches.
    const coaches = new Set();
    assignments.docs.forEach(({ coach }) => {
      coaches.add(coach);
    });

    return Lead.getAllLeadsByCoachList(coaches);
  }

  /**
   * Get the lead by the specified lead id

   * @param {string} id The lead id
   * @returns {Promise<Lead|void>} The lead document if it exists, or else null.
   */
  static async getLeadById(id) {
    const LeadDoc = new Lead(id);
    await LeadDoc.init();
    return LeadDoc.exists ? LeadDoc : null;
  }

  /**
   * Get the most recently assigned lead for the specified user within the last 10 seconds.
   *
   * @param {string} userId - The inside sales user ID.
   * @returns {Promise<Lead>}
   */
  static async getLatestAssignedLeads(userId) {
    const timeThresholdForRecentLead = moment().subtract(MAX_TIME_THRESHOLD_TO_CLAIM_LEAD, 'seconds').toDate();

    const leadsCollection = new Collection(Collections.LEADS, {
      createDocument: ({ id }, opts) => new Lead(id, opts),
      query: (ref) => ref
        .where('assignedTo', '==', userId)
        .where('assignedAt', '>=', timeThresholdForRecentLead)
        .orderBy('assignedAt', 'desc'),
    });
    await leadsCollection.fetch();
    return leadsCollection;
  }

  /**
   * Get all unclaimed leads that are available to claim.
   * This collection listens to changes and filters only by availableToClaim=true.
   *
   * @returns {Promise<Collection<Lead>>}
   */
  static async getUnclaimedLeadsCollection() {
    const unclaimedLeadsCollection = new Collection(Collections.LEADS, {
      createDocument: ({ id }, opts) => new Lead(id, opts),
      query: (ref) => ref
        .where('availableToClaim', '==', true),
    });
    await unclaimedLeadsCollection.fetch();
    return unclaimedLeadsCollection;
  }

  get instagramHandle() {
    return this.data.instagramHandle || '';
  }

  /**
   * Claims a lead by updating the claimant's details. Transaction-safe: only claims if the
   * lead is still assigned to `claimedBy`, so a claim that lands after the server has already
   * reassigned/stripped the lead (e.g. the instant-call reprocess tick) is rejected instead of
   * silently overwriting the doc.
   *
   * @param {string} claimedBy - The ID of the user claiming the lead.
   * @param {string} claimedByUserName - The full name of the user claiming the lead.
   * @returns {Promise<{claimed: boolean}>} - Resolves once the transaction settles.
   */
  claimLead(claimedBy, claimedByUserName) {
    const db = getFirebase().default.firestore();
    return db.runTransaction(async (transaction) => {
      const doc = await transaction.get(this.ref);
      if (!doc.exists || doc.data().assignedTo !== claimedBy) {
        return { claimed: false };
      }

      transaction.update(this.ref, {
        claimedBy,
        claimedAt: new Date(),
        claimedByUserName,
        isAgent: claimedBy,
        isAgentName: claimedByUserName,
        availableToClaim: false,
      });
      return { claimed: true };
    });
  }
}

export default Lead;
export {
  LeadState,
  SalesStage,
  InitialLeadState,
  AllLeadStates,
  AllSalesStages,
  ContactProvider,
  LocalContactProvider,
  LeadHistoryType,
  ClaimRejectReason,
  FinalStatusSalesStages,
  LeadCallOutcome,
  isLockedLeadCallOutcome,
  LeadLostReason,
  LeadFollowUpReason,
  LeadIntentFlag,
  LeadIntentFlagLabel,
  ContactOptions,
  MAX_TIME_THRESHOLD_TO_CLAIM_LEAD,
  ISDashboardSalesStages,
};
